<script lang="ts" setup>
import type { IntelligenceCompany } from '~/composables/api/intelligence/companies/useFetchIntelligenceCompanies'

export interface InvoicingData {
  companyName: string
  companyIdent: string
  address: string
  postNumber: string
  city: string
  email: string
  einvoiceAddress?: string
  einvoiceServiceId?: string
}

defineProps<{
  disabled?: boolean
}>()

const invoicingData = defineModel<InvoicingData>({ required: true, default: {} })

const { t } = useI18n()

const { translateForCountry } = useCountryAwareTranslation()

const { data: config } = useQueryConfig.current()

const isEInvoicingEnabled = computed(() => config.value?.feature_enabled_einvoicing)

const fillFromCompany = ({
  title,
  ident,
  street,
  city,
  post_code,
  einvoice_address,
  einvoice_operator,
}: IntelligenceCompany) => {
  invoicingData.value = {
    ...invoicingData.value,
    companyName: title || invoicingData.value.companyName,
    companyIdent: ident || invoicingData.value.companyIdent,
    address: street || invoicingData.value.address,
    city: city || invoicingData.value.city,
    postNumber: post_code || invoicingData.value.postNumber,
    einvoiceAddress:
      isEInvoicingEnabled.value && einvoice_address ? einvoice_address : invoicingData.value.einvoiceAddress,
    einvoiceServiceId:
      isEInvoicingEnabled.value && einvoice_operator ? einvoice_operator : invoicingData.value.einvoiceServiceId,
  }
}

const isEInvoicingValuesValid = computed(() =>
  (!invoicingData.value.einvoiceAddress?.trim().length && !invoicingData.value.einvoiceServiceId?.trim().length)
  || !!(invoicingData.value.einvoiceAddress?.trim().length && invoicingData.value.einvoiceServiceId?.trim().length),
)

const isValid = computed(() =>
  !!(
    invoicingData.value.companyName.trim().length
    && invoicingData.value.companyIdent.trim().length
    && invoicingData.value.address.trim().length
    && invoicingData.value.city.trim().length
    && invoicingData.value.postNumber.trim().length
    && invoicingData.value.email.trim().length
    && (!isEInvoicingEnabled.value || isEInvoicingValuesValid.value)
  ),
)

defineExpose({
  isValid,
})
</script>

<template>
  <form class="grid">
    <DTField :label="t('company-name')">
      <CompanyNameInput
        v-model="invoicingData.companyName"
        required
        class="[&_fieldset]:min-w-0 [&_input]:min-w-0"
        :disabled
        @company-selected="fillFromCompany"
      />
    </DTField>

    <DTField :label="translateForCountry('company_ident')">
      <DTFieldInput
        v-model="invoicingData.companyIdent"
        wrapper-class="min-w-0"
        class="min-w-0"
        required
        :disabled
      />
    </DTField>

    <DTField
      class="col-span-full"
      :label="t('address')"
    >
      <DTFieldInput
        v-model="invoicingData.address"
        wrapper-class="min-w-0"
        class="min-w-0"
        required
        :disabled
      />
    </DTField>

    <DTField :label="t('post-number')">
      <DTFieldInput
        v-model="invoicingData.postNumber"
        wrapper-class="min-w-0"
        class="min-w-0"
        required
        :disabled
      />
    </DTField>

    <DTField :label="t('city')">
      <DTFieldInput
        v-model="invoicingData.city"
        wrapper-class="min-w-0"
        class="min-w-0"
        required
        :disabled
      />
    </DTField>

    <DTField
      :label="capitalize(t('billing.invoicingEmail'))"
      class="col-span-full"
    >
      <DTFieldInput
        v-model="invoicingData.email"
        wrapper-class="min-w-0"
        class="min-w-0"
        required
        :disabled
      />
    </DTField>

    <DTDivider class="col-span-full my-3" />

    <template v-if="isEInvoicingEnabled">
      <DTField :label="t('billing.einvoice-address')">
        <DTFieldInput
          v-model="invoicingData.einvoiceAddress"
          :required="!!(invoicingData.einvoiceServiceId?.trim().length || invoicingData.einvoiceAddress?.trim().length)"
          wrapper-class="min-w-0"
          class="min-w-0"
          :disabled
        />
      </DTField>

      <DTField :label="t('billing.service-id')">
        <DTFieldInput
          v-model="invoicingData.einvoiceServiceId"
          :required="!!(invoicingData.einvoiceAddress?.trim().length || invoicingData.einvoiceServiceId?.trim().length)"
          wrapper-class="min-w-0"
          class="min-w-0"
          :disabled
        />
      </DTField>
    </template>

    <p class="col-span-full mb-3.5 text-xs text-gray-600">
      {{ t('billing.invoicingInfo') }}
    </p>
  </form>
</template>
